import { useState, useEffect } from "react";

import PageHeader from "../../../components/Page/PageHeader";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import { useGetUsersUsersGet, useKeycloakRedirectKeycloakConsoleGet } from "../../../api/generated/fastAPI";
import { User } from "../../../api/models";
import usePaginate from "../../../hooks/usePaginate";
import DataTable, { TableData, TableHeader } from "../../../components/Table/DataTable";
import SearchBar from "../../../components/Filtering/SearchBar";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import ErrorScreen from "../../../components/Alerts/ErrorScreen";
import Pagination from "../../../components/Filtering/Pagination";
import Buttons from "../../../components/Buttons/Buttons";

import { ReactComponent as RedirectIcon } from '../../../assets/Icons/adornments/Open-New-Tab.svg';
import AccessGate from "../../../components/Auth/AccessGate";
import { admin_access, org_admin_access } from "../../../access/predefinedConditions";
import { useAccess } from "../../../access/useAccess";
import { useCurrentUser } from "../../../providers/CurrentUserProvider";

import CreateUserModal from "../components/CreateUserModal";
import ManageRolesModal from "../components/ManageRolesModal";
import ResetPasswordModal from "../components/ResetPasswordModal";

const userHeaders: TableHeader[] = [
    {
        label: 'Username',
        value: 'username',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Full Name',
        value: 'full_name',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Email Address',
        value: 'email',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Roles',
        value: 'roles',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
]


export default function UserDirectory() {
    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const {
        setItems,
        setItemsPerPage,
        setCurrentPage,
        currentPage,
        paginatedItems,
        startIndex,
        endIndex,
        totalPages,
        paginate,
    } = usePaginate();

    const { canAccess } = useAccess();
    const { hasAcRole } = useCurrentUser();
    const isAdmin = hasAcRole('admin');
    const canManageUsers = canAccess({ or: org_admin_access });

    const getUsersMutation = useGetUsersUsersGet();

    const [ users, setUsers ] = useState<User[]>([]);
    const [ userData, setUserData ] = useState<TableData[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(userHeaders);
    const keycloakLink = useKeycloakRedirectKeycloakConsoleGet();

    // Modal state
    const [createUserOpen, setCreateUserOpen] = useState(false);
    const [rolesModalUser, setRolesModalUser] = useState<User | null>(null);
    const [passwordModalUser, setPasswordModalUser] = useState<User | null>(null);

    const refetchUsers = () => {
        getUsersMutation.refetch();
    };

    useEffect(() => {
        if (getUsersMutation.data) {
            const data = getUsersMutation.data as unknown as User[];
            setUsers(data);

            const items: TableData[] = data.map(user => {
                const { username, first_name, last_name, email, creation_date, updated_date, ac_roles } = user;
                const fullName = `${first_name ? first_name : ''} ${last_name ? last_name : ''}`;

                return {
                    id: username!,
                    fields: [
                        {
                            associatedColumn: 'username',
                            value: username!,
                            alignment: 'left',
                            type: 'text',
                        },
                        {
                            associatedColumn: 'full_name',
                            value: fullName,
                            alignment: 'center',
                            type: 'text',
                        },
                        {
                            associatedColumn: 'email',
                            value: email!,
                            alignment: 'center',
                            type: 'text',
                        },
                        {
                            associatedColumn: 'roles',
                            value: ac_roles ? ac_roles : [],
                            alignment: 'center',
                            type: 'roles',
                        },
                        {
                            associatedColumn: 'creation_date',
                            value: creation_date ? new Date(creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : '',
                            alignment: 'center',
                            type: 'text',
                        },
                        {
                            associatedColumn: 'updated_date',
                            value: updated_date ? new Date(updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : '',
                            alignment: 'center',
                            type: 'text',
                        },
                    ],
                    ...(canManageUsers ? {
                        options: [
                            { label: 'Manage Roles', action: () => setRolesModalUser(user) },
                            { label: 'Reset Password', action: () => setPasswordModalUser(user) },
                        ]
                    } : {}),
                }
            });

            setItems(items);
            setUserData(items);
            
        }

        //eslint-disable-next-line
    }, [getUsersMutation.data]);


    


    return (
        <div style={{maxHeight: 'calc(100vh - 70px)'}} className="h-full overflow-hidden flex flex-col justify-start items-stretch gap-4 p-10">
      

            <PageHeader label='User Directory'>
                <div className="flex items-center gap-3">
                    <AccessGate or={org_admin_access}>
                        <Buttons
                            id="create-user-btn"
                            type="primary"
                            styling="solid"
                            label="Create User"
                            action={() => setCreateUserOpen(true)}
                        />
                    </AccessGate>
                    <AccessGate
                        or={admin_access}
                    >
                        {keycloakLink.data && (
                            <a href={keycloakLink.data as unknown as string} rel="noreferrer" target="_blank" className={`flex justify-between items-center gap-4 px-5 py-3 rounded-lg border border-primary hover:brightness-90 transition-all bg-primary text-white`}>
                                <div className={`flex justify-start items-center gap-2`}>
                                    <div className="w-[18px] h-[18px]"><RedirectIcon /></div>
                                    <p className={`text-[14px] font-medium text-nowrap`}>Keycloak Console</p>
                                </div>
                            </a>
                        )}
                    </AccessGate>
                </div>
            </PageHeader>

            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                    </div>
                </div>
            </div>

            {getUsersMutation.isError && userData.length === 0 ? (
                <ErrorScreen errCode={(getUsersMutation.error as any).code}/>
            ) : (
                <>
                <DataTable 
                    isLoading={getUsersMutation.isLoading} 
                    headers={tableHeaders} 
                    tableData={paginatedItems}
                    setPopupMessage={setPopupMessage}
                />

                <Pagination
                    setItemsPerPage={setItemsPerPage}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    totalPages={totalPages}
                    total={userData.length}
                    paginate={paginate}
                />
                </>
            )}
            
            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>

            {/* Modals */}
            <CreateUserModal
                isOpen={createUserOpen}
                onClose={() => setCreateUserOpen(false)}
                onSuccess={refetchUsers}
                setPopupMessage={setPopupMessage}
            />
            <ManageRolesModal
                isOpen={!!rolesModalUser}
                onClose={() => setRolesModalUser(null)}
                user={rolesModalUser}
                onSuccess={refetchUsers}
                setPopupMessage={setPopupMessage}
                isAdmin={isAdmin}
            />
            <ResetPasswordModal
                isOpen={!!passwordModalUser}
                onClose={() => setPasswordModalUser(null)}
                user={passwordModalUser}
                setPopupMessage={setPopupMessage}
            />
        </div>
    )
}